/**
 * QuickQuestionWidget — right-edge floating "Quick question" tab.
 *
 * Present on every route (public + in-app). Hidden only inside modal-heavy
 * flows where an overlaid tab would interfere (auth, Stripe redirects).
 * The tab is deliberately small — a single icon at rest, expanding into a
 * compact 300px card when tapped. Posts to POST /api/auto-improve/feedback
 * which feeds the nightly Auto-Improve backlog + admin digest.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircleQuestion, X, Send, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Routes where the widget must NOT render (auth, printable flows,
// third-party redirects). Everywhere else — including the public
// landing page — the tab is available on the right edge.
const HIDDEN_EXACT = new Set([
  "/login", "/signup", "/forgot", "/reset", "/auth/callback",
]);
const HIDDEN_PREFIXES = ["/login/", "/vendor/login", "/auth/"];

function shouldHide(pathname) {
  if (HIDDEN_EXACT.has(pathname)) return true;
  return HIDDEN_PREFIXES.some((p) => pathname.startsWith(p));
}

export default function QuickQuestionWidget() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("feature");
  const [body, setBody] = useState("");
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");

  const hidden = useMemo(() => shouldHide(loc.pathname), [loc.pathname]);

  // Pre-fill the email once from localStorage if the user's already logged in.
  useEffect(() => {
    if (email) return;
    try {
      const stored = JSON.parse(localStorage.getItem("crc_user") || "null");
      if (stored?.email) setEmail(stored.email);
    } catch (_) { /* ignore */ }
  }, [email]);

  // Auto-close the "sent" flash after 4s so the panel is reusable.
  useEffect(() => {
    if (state !== "sent") return;
    const t = setTimeout(() => { setState("idle"); setBody(""); setOpen(false); }, 3200);
    return () => clearTimeout(t);
  }, [state]);

  if (hidden) return null;

  async function submit(e) {
    e.preventDefault();
    if (body.trim().length < 6) {
      setErrorMsg("Please share a few more details so we can act on it.");
      setState("error");
      return;
    }
    setState("sending");
    setErrorMsg("");
    try {
      const r = await fetch(`${API}/api/auto-improve/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind,
          body: body.trim(),
          email: email.trim() || undefined,
          path: loc.pathname,
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `Send failed (${r.status})`);
      }
      setState("sent");
    } catch (err) {
      setErrorMsg(err.message || "Something went wrong. Try again in a moment.");
      setState("error");
    }
  }

  return (
    <div
      className="fixed left-0 top-1/2 -translate-y-1/2 z-30 print:hidden hidden sm:block"
      data-testid="quick-question-widget"
    >
      {!open ? (
        // Small icon-only tab that hugs the right edge.
        <button
          onClick={() => setOpen(true)}
          data-testid="quick-question-open"
          aria-label="Ask a quick question"
          title="Quick question"
          className="group flex items-center justify-center w-10 h-10 rounded-r-xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/25 transition-transform hover:translate-x-1"
        >
          <MessageCircleQuestion className="w-5 h-5"/>
        </button>
      ) : (
        <div
          className="w-[300px] rounded-r-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden"
          data-testid="quick-question-panel"
        >
          <div className="flex items-start justify-between px-3.5 py-2.5 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white">
            <div>
              <div className="text-xs font-bold tracking-tight leading-tight">Quick question or idea?</div>
              <div className="text-[10px] text-emerald-100 mt-0.5 leading-tight">We reply within a business day.</div>
            </div>
            <button
              onClick={() => { setOpen(false); setState("idle"); }}
              className="rounded-full p-1 hover:bg-white/10 transition-colors -mr-1"
              aria-label="Close"
              data-testid="quick-question-close"
            >
              <X className="w-3.5 h-3.5"/>
            </button>
          </div>

          {state === "sent" ? (
            <div className="p-5 text-center" data-testid="quick-question-sent">
              <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto">
                <Check className="w-5 h-5"/>
              </div>
              <div className="mt-2 text-sm font-display font-bold text-slate-900">Got it — thanks!</div>
              <p className="mt-0.5 text-xs text-slate-600">
                Your note is on our backlog.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="p-3 space-y-2.5">
              <div className="flex gap-1 p-0.5 bg-slate-100 rounded-md" data-testid="quick-question-kind">
                {[
                  { k: "feature",   label: "Idea" },
                  { k: "bug",       label: "Bug" },
                  { k: "complaint", label: "Frustration" },
                ].map((t) => (
                  <button
                    key={t.k}
                    type="button"
                    onClick={() => setKind(t.k)}
                    data-testid={`quick-question-kind-${t.k}`}
                    className={`flex-1 text-[11px] font-bold py-1 rounded transition-colors ${
                      kind === t.k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                  >{t.label}</button>
                ))}
              </div>

              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="Share your idea, bug, or feedback…"
                className="w-full text-xs p-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                data-testid="quick-question-body"
                required
              />

              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email (optional)"
                className="w-full text-xs p-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                data-testid="quick-question-email"
              />

              {state === "error" && (
                <div className="text-[11px] text-rose-600 bg-rose-50 border border-rose-200 rounded px-2 py-1.5" data-testid="quick-question-error">
                  {errorMsg}
                </div>
              )}

              <button
                type="submit"
                disabled={state === "sending"}
                data-testid="quick-question-submit"
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white font-bold text-xs transition-colors"
              >
                {state === "sending" ? "Sending…" : (<>Send <Send className="w-3.5 h-3.5"/></>)}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
